import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import bonusService, {
  formatSubjectLabel,
} from "../../services/bonusService";

const STATUS_MAP = {
  COMPLETE: "Ready",
  MISSING_DATA: "Not defined yet",
  NOT_CALCULATED: "Not calculated",
  OVERRIDDEN: "Adjusted",
};

const SOURCE_LABELS = {
  educationReport: "Education report",
  semesterReview: "Semester review",
  schoolGrade: "School grades",
};

function roleName(role) {
  const raw = typeof role === "string" ? role : role?.name || role?.authority;
  return (raw || "").toUpperCase().replace(/^ROLE_/, "");
}

function StatusPill({ status }) {
  const isReady = status === "COMPLETE" || status === "OVERRIDDEN";

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
        isReady
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-500 border-gray-200"
      }`}
    >
      <i
        className={`fas ${isReady ? "fa-check" : "fa-clock"} mr-1.5 text-[10px]`}
      ></i>
      {STATUS_MAP[status] || status || "Open"}
    </span>
  );
}

function SummaryCard({ icon, label, value, hint, tone = "default" }) {
  const valueClass = tone === "brand" ? "text-sunrise-red" : "text-grey-dusk";

  return (
    <div className="card-brand p-4 bg-white rounded-2xl border border-pale-dawn">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`text-xl font-bold mt-1 truncate ${valueClass}`}>
            {value}
          </p>
          {hint && (
            <p className="text-xs text-gray-500 mt-1 truncate">{hint}</p>
          )}
        </div>
        <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
          <i className={icon}></i>
        </div>
      </div>
    </div>
  );
}

function SourceRow({ label, status, percent, extra }) {
  const isReady = status === "COMPLETE";

  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
            isReady ? "bg-green-50 text-green-600" : "bg-gray-50 text-gray-400"
          }`}
        >
          <i
            className={`fas ${isReady ? "fa-check" : "fa-minus"} text-[10px]`}
          ></i>
        </span>
        <span className="text-sm text-grey-dusk truncate">{label}</span>
      </div>

      <div className="text-right shrink-0">
        <div
          className={`text-sm font-semibold ${isReady ? "text-grey-dusk" : "text-gray-400"}`}
        >
          {isReady ? bonusService.formatPercent(percent) : "Open"}
        </div>
        {extra && <div className="text-[11px] text-gray-400">{extra}</div>}
      </div>
    </div>
  );
}

function SemesterCard({ semester, open, onToggle }) {
  const isReady =
    semester.status === "COMPLETE" || semester.status === "OVERRIDDEN";
  const avg =
    semester.schoolGradeAverage != null
      ? `Avg ${Number(semester.schoolGradeAverage).toFixed(2)}`
      : null;

  const completedSources = [
    semester.educationReportStatus,
    semester.semesterReviewStatus,
    semester.schoolGradeStatus,
  ].filter((status) => status === "COMPLETE").length;

  return (
    <div className="card-brand bg-white rounded-2xl border border-pale-dawn overflow-hidden">
      {/* Semester header - toggles the breakdown below */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30 hover:from-pale-dawn/40 transition-colors"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <i
                className={`fas fa-chevron-right text-sunrise-red text-sm transition-transform ${
                  open ? "rotate-90" : ""
                }`}
              ></i>
              <h3 className="text-lg font-bold text-grey-dusk">
                {semester.semesterLabel ||
                  `Semester ${semester.semesterNumber}`}
              </h3>
              <StatusPill status={semester.status} />
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              {completedSources}/3 sources complete
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 min-w-full lg:min-w-[380px]">
            <div className="rounded-xl bg-white border border-pale-dawn px-3 py-2">
              <p className="text-[11px] text-gray-500">Semester %</p>
              <p className="text-sm font-bold text-grey-dusk">
                {semester.semesterPercent != null
                  ? bonusService.formatPercent(semester.semesterPercent)
                  : "Open"}
              </p>
            </div>

            <div className="rounded-xl bg-white border border-pale-dawn px-3 py-2">
              <p className="text-[11px] text-gray-500">
                Configured amount = 100%
              </p>
              <p className="text-sm font-bold text-grey-dusk">
                {bonusService.formatCHF(semester.configuredAmount)}
              </p>
            </div>

            <div className="rounded-xl bg-white border border-pale-dawn px-3 py-2">
              <p className="text-[11px] text-gray-500">Payout</p>
              <p
                className={`text-sm font-bold ${isReady ? "text-sunrise-red" : "text-gray-400"}`}
              >
                {isReady ? bonusService.formatCHF(semester.finalBonus) : "Open"}
              </p>
            </div>
          </div>
        </div>
      </button>

      {/* Source breakdown */}
      {open && (
        <div className="p-5">
          <div className="rounded-2xl border border-pale-dawn bg-white p-4 min-w-0">
            <div className="divide-y divide-pale-dawn/70">
              <SourceRow
                label={SOURCE_LABELS.educationReport}
                status={semester.educationReportStatus}
                percent={semester.educationReportPercent}
              />
              <SourceRow
                label={SOURCE_LABELS.semesterReview}
                status={semester.semesterReviewStatus}
                percent={semester.semesterReviewPercent}
              />
              <SourceRow
                label={SOURCE_LABELS.schoolGrade}
                status={semester.schoolGradeStatus}
                percent={semester.schoolGradePercent}
                extra={avg}
              />
            </div>

            {(semester.grades || []).length > 0 && (
              <details className="mt-3 group">
                <summary className="cursor-pointer list-none text-xs font-semibold text-sunrise-red flex items-center justify-between rounded-lg bg-ice-white border border-pale-dawn px-3 py-2">
                  <span>
                    <i className="fas fa-table mr-2"></i>
                    Show definitive grades
                  </span>
                  <i className="fas fa-chevron-down group-open:rotate-180 transition-transform"></i>
                </summary>

                <div className="mt-2 rounded-xl border border-pale-dawn overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-ice-white text-gray-500">
                        <th className="text-left px-3 py-2 font-semibold">
                          Subject
                        </th>
                        <th className="text-right px-3 py-2 font-semibold">
                          Grade
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-pale-dawn">
                      {semester.grades.map((g, i) => (
                        <tr
                          key={`${semester.semesterNumber}-${g.subjectId || i}-${i}`}
                        >
                          <td className="px-3 py-2 text-grey-dusk">
                            {formatSubjectLabel(g)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-grey-dusk">
                            {g.definitiveGrade != null
                              ? Number(g.definitiveGrade).toFixed(1)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GradeBonusTable() {
  const rows = [
    [
      ["6", "150"],
      ["5", "100"],
    ],
    [
      ["5.9", "145"],
      ["4.9", "95"],
    ],
    [
      ["5.8", "140"],
      ["4.8", "90"],
    ],
    [
      ["5.7", "135"],
      ["4.7", "80"],
    ],
    [
      ["5.6", "130"],
      ["4.6", "70"],
    ],
    [
      ["5.5", "125"],
      ["4.5", "60"],
    ],
    [
      ["5.4", "120"],
      ["4.4", "50"],
    ],
    [
      ["5.3", "115"],
      ["4.3", "40"],
    ],
    [
      ["5.2", "110"],
      ["4.2", "30"],
    ],
    [
      ["5.1", "105"],
      ["4.1", "20"],
    ],
    [
      ["4", "10"],
      ["<4", "0"],
    ],
  ];

  return (
    <details className="card-brand bg-white rounded-2xl border border-pale-dawn overflow-hidden group">
      <summary className="cursor-pointer list-none px-5 py-4 bg-gradient-to-r from-ice-white to-pale-dawn/30 border-b border-pale-dawn">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-grey-dusk flex items-center">
              <i className="fas fa-graduation-cap text-sunrise-red mr-3"></i>
              School grades vs. bonus %
            </h2>
            <p className="text-sm text-slate-cloud mt-1">
              See how the definitive school grade average is converted into
              bonus percentage.
            </p>
          </div>
          <i className="fas fa-chevron-down text-slate-cloud group-open:rotate-180 transition-transform"></i>
        </div>
      </summary>

      <div className="p-5">
        <div className="overflow-hidden rounded-2xl border border-pale-dawn">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-sunrise-red text-white">
                <th className="px-4 py-2 text-left font-semibold">Grade</th>
                <th className="px-4 py-2 text-left font-semibold">Bonus %</th>
                <th className="px-4 py-2 text-left font-semibold">Grade</th>
                <th className="px-4 py-2 text-left font-semibold">Bonus %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pale-dawn">
              {rows.map((row, index) => (
                <tr
                  key={index}
                  className="hover:bg-ice-white/70 transition-colors"
                >
                  {row.map(([grade, percent]) => (
                    <React.Fragment key={grade}>
                      <td className="px-4 py-2 font-medium text-grey-dusk">
                        {grade}
                      </td>
                      <td className="px-4 py-2 font-semibold text-sunrise-red">
                        {percent}%
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

export default function MyBonusPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isLearner = (user?.roles || []).some((r) => roleName(r) === "LERNENDE");

  useEffect(() => {
    if (!isLearner) return;

    let active = true;

    bonusService
      .getMyBonus()
      .then((res) => {
        if (!active) return;
        setData(res);
      })
      .catch(() => {
        if (!active) return;
        setError("Failed to load your bonus data.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isLearner]);

  const semesters = useMemo(() => data?.semesters || [], [data?.semesters]);

  const currentSemester = useMemo(() => {
    if (!semesters.length) return null;
    return (
      semesters.find((s) => s.semesterNumber === data?.currentSemesterNumber) ||
      semesters.find((s) => s.semesterLabel === data?.currentSemesterLabel) ||
      semesters[semesters.length - 1]
    );
  }, [data, semesters]);

  // Only one semester is expanded at a time, and it starts on the one the
  // learner is currently in. Past semesters stay collapsed until opened.
  const [openSemester, setOpenSemester] = useState(null);

  useEffect(() => {
    if (currentSemester) setOpenSemester(currentSemester.semesterNumber);
  }, [currentSemester]);

  if (!isLearner) return <Navigate to="/bonus" replace />;

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
          <div className="card-brand-elevated p-6 bg-white rounded-2xl border border-pale-dawn">
            <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-80 bg-gray-200 rounded mt-3 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="card-brand h-24 bg-white rounded-2xl animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-6">
        <div className="card-brand p-6 max-w-lg bg-white rounded-2xl text-center">
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        {/* Header */}
        <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 rounded-2xl border border-pale-dawn">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-grey-dusk flex items-center">
                <i className="fas fa-coins text-sunrise-red mr-4"></i>
                My Bonus
              </h1>
              <p className="text-sm md:text-base text-slate-cloud">
                See your bonus history and how your bonus is calculated each semester.
              </p>
            </div>

            <div className="rounded-2xl bg-white border border-pale-dawn px-4 py-3 min-w-[220px]">
              <p className="text-xs text-gray-500">Current semester</p>
              <p className="text-lg font-bold text-grey-dusk mt-1">
                {data?.currentSemesterLabel || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Calculation info */}
        <div className="card-brand p-5 bg-white rounded-2xl border border-pale-dawn">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
              <i className="fas fa-calculator"></i>
            </div>

            <div className="flex-1">
              <h2 className="text-lg font-bold text-grey-dusk">
                How your bonus is calculated
              </h2>

              <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 text-sm text-gray-600">
                <div className="rounded-xl bg-ice-white border border-pale-dawn px-4 py-3">
                  <div className="font-semibold text-grey-dusk mb-2">
                    Semester %
                  </div>
                  <div className="flex items-center gap-3">
                    <span>=</span>
                    <div className="inline-flex flex-col items-center leading-tight text-center">
                      <span>
                        Bildungsbericht + Semestergespräch + School grades
                      </span>
                      <span className="w-full border-t border-grey-dusk my-1"></span>
                      <span>3</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-ice-white border border-pale-dawn px-4 py-3">
                  <div className="font-semibold text-grey-dusk mb-2">
                    Bonus CHF
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>=</span>
                    <span>Configured amount</span>
                    <span>×</span>
                    <span>Semester %</span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                Each semester is calculated independently. School grades use
                definitive checked or finalized grades.
              </p>
            </div>
          </div>
        </div>

        <GradeBonusTable />
        {/* Current semester summary */}
        {currentSemester && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard
              icon="fas fa-calendar-alt"
              label="Current semester"
              value={
                currentSemester.semesterLabel ||
                `Semester ${currentSemester.semesterNumber}`
              }
            />
            <SummaryCard
              icon="fas fa-percent"
              label="Semester %"
              value={
                currentSemester.semesterPercent != null
                  ? bonusService.formatPercent(currentSemester.semesterPercent)
                  : "Open"
              }
            />
            <SummaryCard
              icon="fas fa-wallet"
              label="Configured amount = 100%"
              value={bonusService.formatCHF(currentSemester.configuredAmount)}
            />
            <SummaryCard
              icon="fas fa-money-bill-wave"
              label="Expected payout"
              value={
                currentSemester.status === "COMPLETE" ||
                currentSemester.status === "OVERRIDDEN"
                  ? bonusService.formatCHF(currentSemester.finalBonus)
                  : "Open"
              }
              tone="brand"
            />
          </div>
        )}

        {/* Payout overview table */}
        <div className="card-brand bg-white rounded-2xl border border-pale-dawn overflow-hidden">
          <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <h2 className="text-lg font-bold text-grey-dusk flex items-center">
              <i className="fas fa-list text-sunrise-red mr-3"></i>
              Payout overview
            </h2>
          </div>

          <div className="divide-y divide-pale-dawn">
            {semesters.map((s) => {
              const isReady =
                s.status === "COMPLETE" || s.status === "OVERRIDDEN";

              return (
                <div
                  key={s.semesterNumber}
                  className="px-5 py-3 grid grid-cols-1 md:grid-cols-5 gap-2 md:items-center hover:bg-ice-white/60 transition-colors"
                >
                  <div className="font-semibold text-grey-dusk">
                    {s.semesterLabel || `Semester ${s.semesterNumber}`}
                  </div>
                  <div>
                    <StatusPill status={s.status} />
                  </div>
                  <div className="text-sm text-gray-600">
                    {s.semesterPercent != null
                      ? bonusService.formatPercent(s.semesterPercent)
                      : "Open"}
                  </div>
                  <div className="text-sm text-gray-600">
                    {bonusService.formatCHF(s.configuredAmount)}
                  </div>
                  <div
                    className={`text-sm font-bold md:text-right ${isReady ? "text-sunrise-red" : "text-gray-400"}`}
                  >
                    {isReady ? bonusService.formatCHF(s.finalBonus) : "Open"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Semester detail cards */}
        <div className="space-y-4">
          {semesters.map((s) => (
            <SemesterCard
              key={`detail-${s.semesterNumber}`}
              semester={s}
              open={openSemester === s.semesterNumber}
              onToggle={() =>
                setOpenSemester((prev) =>
                  prev === s.semesterNumber ? null : s.semesterNumber,
                )
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
